import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  copilotApi,
  type AgentBrief,
  type CopilotDashboardSummary,
  type CopilotInsight,
  type DailyBriefing,
} from '../../api/copilot';
import {
  workflowApi,
  type ActivityEntry,
  type AgentDefinition,
  type ApprovalRequest,
  type AutomationRule,
  type OrchestrationDashboard,
  type OrchestrationNotification,
  type OrchestrationTask,
  type SchedulerJob,
  type WorkflowExecution,
} from '../../api/workflow';

const WORKSPACE = (getWorkspaceId() ?? '');

type Tab =
  | 'overview'
  | 'tasks'
  | 'approvals'
  | 'executions'
  | 'rules'
  | 'agents'
  | 'copilot'
  | 'notifications';

export function OrchestrationDashboardPage() {
  const location = useLocation();
  const initialTab = useMemo<Tab>(() => (location.pathname === '/copilot' ? 'copilot' : 'overview'), [location.pathname]);

  const [tab, setTab] = useState<Tab>(initialTab);
  const [dashboard, setDashboard] = useState<OrchestrationDashboard | null>(null);
  const [copilotDash, setCopilotDash] = useState<CopilotDashboardSummary | null>(null);
  const [briefing, setBriefing] = useState<DailyBriefing | null>(null);
  const [insights, setInsights] = useState<CopilotInsight[]>([]);
  const [agentBriefs, setAgentBriefs] = useState<AgentBrief[]>([]);
  const [tasks, setTasks] = useState<OrchestrationTask[]>([]);
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [schedulerJobs, setSchedulerJobs] = useState<SchedulerJob[]>([]);
  const [notifications, setNotifications] = useState<OrchestrationNotification[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [
        dash,
        cDash,
        brief,
        ins,
        briefAgents,
        taskList,
        approvalList,
        execList,
        ruleList,
        agentList,
        jobs,
        notifs,
        act,
      ] = await Promise.all([
        workflowApi.dashboard(WORKSPACE),
        copilotApi.dashboard(WORKSPACE),
        copilotApi.briefing(WORKSPACE),
        copilotApi.insights(WORKSPACE),
        copilotApi.agents(WORKSPACE),
        workflowApi.listTasks(WORKSPACE),
        workflowApi.approvalCenter(WORKSPACE),
        workflowApi.executionMonitor(WORKSPACE),
        workflowApi.listAutomationRules(WORKSPACE),
        workflowApi.listAgents(WORKSPACE),
        workflowApi.listSchedulerJobs(WORKSPACE),
        workflowApi.listNotifications(true),
        workflowApi.listActivity(15),
      ]);
      setDashboard(dash);
      setCopilotDash(cDash);
      setBriefing(brief);
      setInsights(ins);
      setAgentBriefs(briefAgents);
      setTasks(taskList);
      setApprovals(approvalList);
      setExecutions(execList);
      setRules(ruleList);
      setAgents(agentList);
      setSchedulerJobs(jobs);
      setNotifications(notifs);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load orchestration dashboard');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const seedSample = async () => {
    setBusy(true);
    try {
      await workflowApi.createAutomationRule({
        workspace_id: WORKSPACE,
        name: 'Low inventory alert',
        domain: 'inventory',
        condition_expr: 'inventory < threshold',
        then_action: 'notify_operations',
        then_notify: 'in_app',
        severity: 'high',
      });
      await workflowApi.createSchedulerJob({
        workspace_id: WORKSPACE,
        name: 'Daily copilot briefing',
        frequency: 'daily',
        timezone: 'UTC',
      });
      const evt = await workflowApi.ingestEvent({
        workspace_id: WORKSPACE,
        source: 'inventory',
        event_type: 'stock_below_threshold',
        payload: { sku: 'WIDGET-001', qty: 12, threshold: 50 },
      });
      await workflowApi.processEvent(evt.id);
      await workflowApi.createTask({
        workspace_id: WORKSPACE,
        agent_type: 'inventory',
        title: 'Replenish Widget Set SKU',
        business_reason: 'Stock below safety threshold — 12 units remaining',
        suggested_action: 'Create replenishment PO and route through release management',
        estimated_impact: 4200,
        requires_approval: true,
        priority: 'high',
        evidence: ['inventory: WIDGET-001 qty=12', 'threshold=50', '7-day velocity=18/day'],
      });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const approveTask = async (id: string) => {
    await workflowApi.transitionTask(id, { status: 'in_progress', actor: 'operator', comment: 'Approved for action' });
    await refresh();
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'tasks', label: 'Task Center' },
    { id: 'approvals', label: 'Approvals' },
    { id: 'executions', label: 'Executions' },
    { id: 'rules', label: 'Automation Rules' },
    { id: 'agents', label: 'Agent Explorer' },
    { id: 'copilot', label: 'Copilot' },
    { id: 'notifications', label: 'Notifications' },
  ];

  const title = location.pathname === '/copilot' ? 'Enterprise AI Copilot' : 'Workflow Orchestration';
  const subtitle =
    location.pathname === '/copilot'
      ? 'Daily briefing, insights & governed agent coordination — FCP-00009'
      : 'Event-driven automation, workflows, tasks & governance — FCP-00009';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={seedSample}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Seed Event + Rule + Task
          </button>
          <button type="button" onClick={refresh} className="rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm ${
              tab === t.id ? 'bg-indigo-100 font-medium text-indigo-800' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Running Executions" value={dashboard.running_executions} />
            <StatCard label="Open Tasks" value={dashboard.open_tasks} />
            <StatCard label="Pending Approvals" value={dashboard.pending_approvals} />
            <StatCard label="Events (24h)" value={dashboard.events_processed_24h} />
            <StatCard label="Active Agents" value={dashboard.active_agents} />
            <StatCard label="Scheduled Jobs" value={dashboard.scheduled_jobs} />
            <StatCard label="AI Cost Today" value={`$${dashboard.ai_cost_today.toFixed(2)}`} />
            <StatCard label="Unread Alerts" value={dashboard.unread_notifications} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title="Recent Activity">
              <ActivityList items={activity.length ? activity : dashboard.recent_activity ?? []} />
            </Panel>
            <Panel title="Scheduler Jobs">
              {schedulerJobs.length === 0 ? (
                <p className="text-sm text-slate-500">No scheduled jobs yet.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {schedulerJobs.map((j) => (
                    <li key={j.id} className="py-2 text-sm">
                      <span className="font-medium">{j.name}</span>
                      <span className="ml-2 text-slate-500">{j.frequency}</span>
                      {j.next_run_at && <span className="ml-2 text-xs text-slate-400">next: {j.next_run_at}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </div>
      )}

      {tab === 'tasks' && (
        <Panel title="Task Center">
          {tasks.length === 0 ? (
            <p className="text-sm text-slate-500">No tasks. Seed a sample or ingest an event.</p>
          ) : (
            <div className="space-y-3">
              {tasks.map((t) => (
                <div key={t.id} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{t.title}</p>
                      <p className="mt-1 text-sm text-slate-600">{t.business_reason}</p>
                      <p className="mt-2 text-sm text-indigo-700">{t.suggested_action}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 text-xs">
                      <Badge text={t.priority} tone="amber" />
                      <Badge text={t.status} tone={t.status === 'open' ? 'blue' : 'green'} />
                      {t.requires_approval && <Badge text="approval required" tone="red" />}
                    </div>
                  </div>
                  {t.status === 'open' && t.requires_approval && (
                    <button
                      type="button"
                      onClick={() => approveTask(t.id)}
                      className="mt-3 rounded bg-emerald-600 px-3 py-1 text-xs text-white hover:bg-emerald-700"
                    >
                      Approve &amp; Start
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {tab === 'approvals' && (
        <Panel title="Approval Center">
          {approvals.length === 0 ? (
            <p className="text-sm text-slate-500">No pending approvals.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2">Execution</th>
                  <th>Node</th>
                  <th>Status</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100">
                    <td className="py-2 font-mono text-xs">{a.execution_id.slice(0, 8)}…</td>
                    <td>{a.node_id}</td>
                    <td><Badge text={a.status} tone="amber" /></td>
                    <td className="text-slate-500">{a.updated_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {tab === 'executions' && (
        <Panel title="Execution Monitor">
          {executions.length === 0 ? (
            <p className="text-sm text-slate-500">No workflow executions in progress.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2">Workflow</th>
                  <th>Status</th>
                  <th>Current Node</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100">
                    <td className="py-2 font-mono text-xs">{e.workflow_id.slice(0, 8)}…</td>
                    <td><Badge text={e.status} tone={e.status === 'failed' ? 'red' : 'blue'} /></td>
                    <td>{e.current_node ?? '—'}</td>
                    <td className="text-slate-500">{e.started_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {tab === 'rules' && (
        <Panel title="Automation Rules">
          {rules.length === 0 ? (
            <p className="text-sm text-slate-500">No automation rules configured.</p>
          ) : (
            <div className="space-y-3">
              {rules.map((r) => (
                <div key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                  <p className="font-medium">{r.name}</p>
                  <p className="mt-1 font-mono text-xs text-slate-600">IF {r.condition_expr}</p>
                  <p className="mt-1 text-indigo-700">THEN {r.then_action}</p>
                  {r.severity && <Badge text={r.severity} tone="amber" />}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {tab === 'agents' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(agents.length ? agents : []).map((a) => (
            <div key={a.id} className="rounded-lg border border-slate-200 p-4">
              <p className="font-medium text-slate-900">{a.name}</p>
              <p className="text-xs uppercase text-slate-400">{a.type}</p>
              <p className="mt-2 text-sm text-slate-600">{a.description}</p>
              {a.requires_approval && <Badge text="governed" tone="blue" />}
            </div>
          ))}
          {agents.length === 0 &&
            agentBriefs.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-200 p-4">
                <p className="font-medium text-slate-900">{a.name}</p>
                <p className="text-xs uppercase text-slate-400">{a.type}</p>
                <p className="mt-2 text-sm text-slate-600">{a.description}</p>
                <p className="mt-2 text-xs text-slate-500">{a.tasks_open} open tasks · {a.status}</p>
              </div>
            ))}
        </div>
      )}

      {tab === 'copilot' && (
        <div className="space-y-6">
          {briefing && (
            <Panel title={`Daily Briefing — ${briefing.date}`}>
              <p className="mb-4 text-slate-700">{briefing.greeting}</p>
              <div className="grid gap-4 md:grid-cols-2">
                {briefing.sections.map((s) => (
                  <div key={s.title} className="rounded-lg bg-slate-50 p-4">
                    <p className="font-medium text-slate-900">{s.title}</p>
                    <p className="mt-1 text-sm text-slate-600">{s.summary}</p>
                    {s.items && (
                      <ul className="mt-2 list-inside list-disc text-sm text-slate-600">
                        {s.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {s.requires_approval && <Badge text="requires approval" tone="red" />}
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard label="Active Sessions" value={copilotDash?.active_sessions ?? 0} />
            <StatCard label="Recommendations" value={copilotDash?.total_recommendations ?? 0} />
            <StatCard label="Pending Insights" value={copilotDash?.pending_insights ?? 0} />
          </div>

          <Panel title="Copilot Insights">
            {(insights.length ? insights : copilotDash?.insights ?? []).length === 0 ? (
              <p className="text-sm text-slate-500">No insights yet.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {(insights.length ? insights : copilotDash?.insights ?? []).map((i) => (
                  <div key={i.id} className="rounded-lg border border-slate-200 p-4">
                    <div className="flex items-start justify-between">
                      <p className="font-medium">{i.title}</p>
                      <span className="text-xs text-slate-500">{(i.confidence * 100).toFixed(0)}% conf.</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{i.description}</p>
                    {i.action && <p className="mt-2 text-sm text-indigo-700">{i.action}</p>}
                    {i.requires_approval && <Badge text="approval required" tone="red" />}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {tab === 'notifications' && (
        <Panel title="Notification Center">
          {notifications.length === 0 ? (
            <p className="text-sm text-slate-500">No unread notifications.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {notifications.map((n) => (
                <li key={n.id} className="flex items-start justify-between py-3">
                  <div>
                    <p className="font-medium text-slate-900">{n.title}</p>
                    {n.body && <p className="text-sm text-slate-600">{n.body}</p>}
                    <p className="mt-1 text-xs text-slate-400">{n.created_at}</p>
                  </div>
                  <Badge text={n.priority} tone={n.priority === 'critical' ? 'red' : 'blue'} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-medium text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function Badge({ text, tone }: { text: string; tone: 'blue' | 'green' | 'amber' | 'red' }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-emerald-100 text-emerald-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
  };
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${colors[tone]}`}>{text}</span>;
}

function ActivityList({ items }: { items: ActivityEntry[] }) {
  if (items.length === 0) {
    return <p className="text-sm text-slate-500">No recent activity.</p>;
  }
  return (
    <ul className="divide-y divide-slate-100">
      {items.map((a) => (
        <li key={a.id} className="py-2 text-sm">
          <span className="font-medium text-slate-800">{a.action}</span>
          <span className="text-slate-600"> — {a.message}</span>
          <p className="text-xs text-slate-400">{a.created_at}</p>
        </li>
      ))}
    </ul>
  );
}
